import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import { type EmbeddedPaymentElementResult } from '@stripe/stripe-react-native';
import {
  AnimatedDialog,
  AnimatedDialogScrollView,
  AnimatedDialogScrollViewMounted,
  AnimatedDialogView,
  CUSTOM_NAVIGATION_HEADER_HEIGHT,
  theme,
  useResponsive,
} from '@sg/garnish';
import { LoyaltyRewardTerms } from '@sg/shared-fe/features/loyalty';

import { Loyalty } from '@order/features/loyalty';
import { adjustWantedTime, Bag } from '@order/features/ordering';
import {
  fallbackNoPaymentIsNeeded,
  StripePaymentButton,
  StripePaymentElement,
  StripePaymentMethod,
  useNotifyStripeCancel,
  useNotifyStripeError,
  useStripePayment,
} from '@order/integrations/stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';
import { ignoreTimezone } from '@order/utils';

import { useBraze } from '../../../../context/Braze';
import { useLoyaltyContent, useLoyaltyPointsRewards } from '../../../../hooks';
import {
  useBagContent,
  type useOrderState,
  useValidProducts,
} from '../../hooks';
import { getOrderPlaybackConfig } from '../../utils';
import { BagDeliveryPreferencesForm } from '../BagDeliveryPreferencesForm';
import { BagTipping } from '../BagTipping';
import { BagAnimatedDialogHeader } from './BagAnimatedDialogHeader';

/**
 * Animated dialog that shows up when choosing options in the bag.
 */
export const BagAnimatedDialog = (props: BagAnimatedDialogProps) => {
  const { orderState } = props;

  const { track } = useTelemetry();
  const { logCustomEvent } = useBraze();
  const { currentBreakpoint, minWidth, currentHeight, match } = useResponsive();

  // ─── Flags ────────────────────────────────────────────────────────────────

  const {
    isChangingTime,
    isChangingDeliveryPreferences,
    isSubmittingOrder,
    isApplyingGiftCard,
    isPerformingBagAction,
    isShowingCheckoutLedger,
    isShowingOrderPlayback,
    isShowingModal,
    isShowingNextDayOrderNotice,
    isChangingPaymentMethod,
    isShowingGiftCardForm,
    isShowingLoyaltyInfo,
    isShowingLoyaltyTerms,
    isPostOrder,
    requestOrderSubmission,
    requestOrderPlayback,
  } = orderState;

  // ─── Context ──────────────────────────────────────────────────────────────

  const {
    wantedTime,
    selectedTip,
    availableTips,
    cart,
    paymentMethods,
    availableCredit,
    availablePoints,
    shouldUseCredit,
    rewards,
    rewardTermsBeingShown,
    total,
    creditToBeApplied,
    stripeIntentConfig,
    stripeElementConfig,
  } = orderState;

  // ─── Cart ─────────────────────────────────────────────────────────────────

  const {
    orderChannel = 'pickup',
    restaurantId = '',
    locationName = '',
    deliveryOrderDetail,
    deliveryPreferences,
    wantedTimes,
    ledger,
    lineItems,
    maxTipAmount,
  } = cart;

  const products = useValidProducts(lineItems);

  // ─── Tipping ──────────────────────────────────────────────────────────────

  const isSubmissionDisabled =
    isPerformingBagAction || selectedTip > maxTipAmount;

  // ─── Actions ──────────────────────────────────────────────────────────────

  const {
    goBack,
    cancelRequest,
    changeDeliveryPreferences,
    changeTime,
    changeTip,
    requestPaymentMethodChange,
    finishPaymentSelection,
    changeCreditUsage,
    requestGiftCardForm,
    submitGiftCardForm,
    setIsGooglePayWeb,
    setConfirmStripe,
    startStripeConfirmation,
  } = orderState;

  // ─── Contentful ──────────────────────────────────────────────────────

  const {
    bagDiscountBody: discountInfo,
    homeProgressBenefitsDisclaimer: benefitsDisclaimer,
  } = useLoyaltyContent({
    availablePoints,
  });

  const {
    orderPlaybackTitle,
    orderPlaybackFooterTitle,
    orderPlaybackFooterLoading,
    orderPlaybackFooterCta,
    orderPlaybackCta,
    placeOrderCta,
    nextDayOrderWarningRegular,
    nextDayOrderWarningOutpost,
  } = useBagContent();

  // ─── Points Rewards ───────────────────────────────────────────────────────

  const { pointsRewards, isFetchingPointsRewards, refetchPointsRewards } =
    useLoyaltyPointsRewards();

  // ─── Rewards Terms ─────────────────────────────────────────────────────────

  const rewardBeingShownTerms = useMemo(
    () => rewards.find((reward) => reward.id === rewardTermsBeingShown),
    [rewardTermsBeingShown, rewards],
  );

  // ─── Animated Dialog Scrolling ────────────────────────────────────────────
  // We track how much the dialog was scrolled to animate the header opacity.

  const scrollOffsetSV = useSharedValue(0);
  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollAmount = event.nativeEvent.contentOffset.y;

      // Ignore auto scroll due to the dialog header.
      if (scrollAmount === CUSTOM_NAVIGATION_HEADER_HEIGHT) return;

      scrollOffsetSV.value = scrollAmount;
    },
    [scrollOffsetSV],
  );

  const resetScrollOffset = useCallback(() => {
    scrollOffsetSV.value = 0;
  }, [scrollOffsetSV]);

  // ─── Height Without Disclaimers ───────────────────────────────────────────
  // This section measures the checkout layout without the disclaimers at the bottom.
  // This will be the max height of the animated dialog, so disclaimers aren't immediately shown.

  const ledgerDialogHeight = useSharedValue(0);
  const ledgerContentHeight = useSharedValue(0);
  const ledgerContentStyle = useAnimatedStyle(() => ({ maxHeight: ledgerContentHeight.value }), [ledgerContentHeight]); // prettier-ignore
  const measureCheckoutWithoutDisclaimers = useCallback(
    (event: LayoutChangeEvent) => {
      const layoutHeight = event.nativeEvent.layout.height;
      const withHeader = layoutHeight + theme.spacing['18'];

      ledgerDialogHeight.value = withHeader;
      ledgerContentHeight.value = layoutHeight;
    },
    [ledgerDialogHeight, ledgerContentHeight],
  );

  // ─── Scroll View ──────────────────────────────────────────────────────────

  const scrollViewProps = useMemo(
    () => ({
      contentContainerStyle: styles.dialogContentInnerContainer,
      automaticallyAdjustKeyboardInsets: true,
      stickyHeaderIndices: [0],
      onScroll: handleOnScroll,
    }),
    [handleOnScroll],
  );

  const scrollViewFullHeightProps = useMemo(
    () => ({
      style: styles.fullHeight,
      stickyHeaderIndices: undefined,
      automaticallyAdjustKeyboardInsets: false,
    }),
    [],
  );

  // ─── Localization ────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const dismissModal = formatMessage(messages.dismissModal);
  const nextDayOrderWarning =
    orderChannel === 'outpost'
      ? nextDayOrderWarningOutpost
      : nextDayOrderWarningRegular;

  // ─── Stripe ──────────────────────────────────────────────────────────

  const isIntentConfirmationEnabled = useFeatureFlag('CELS-4725-stripe-client-intent-confirmation-enabled'); // prettier-ignore

  const trackStripeUsage = useCallback(
    (payload: unknown) => {
      track('checkout.stripe-usage', payload);
    },
    [track],
  );

  const {
    isGooglePay,
    embeddedPaymentElementView,
    selectedPaymentMethod,
    selectedPaymentImage,
    selectedPaymentIcon,
    stripeError,
    confirmStripe,
  } = useStripePayment({
    isIntentConfirmationEnabled,
    total,
    paymentMethods,
    stripeIntentConfig,
    stripeElementConfig,
  });

  const isGooglePayWeb = isGooglePay && Platform.OS === 'web';
  const isGooglePayAndroid = isGooglePay && Platform.OS === 'android';
  const shouldConfirmIntent = isIntentConfirmationEnabled && !isGooglePayWeb;

  useTrackEventEffect({
    name: 'checkout.stripe-usage',
    payload: { event: 'stripe_failure', error: stripeError },
    skip: !stripeError,
  });

  /**
   * ─── Order Submission ────────────────────────────────────────────────
   * Order submission includes playback, confirmation and submission.
   * Confirmation -> Creating and confirming payment intent OR creating payment method.
   * Playback -> Showing order playback (customer can go back).
   * Submission -> Calling the submission mutation to the backend.
   * When {isIntentConfirmationEnabled}, it follows this flow: Playback -> Confirmation -> Submission
   * When it is disabled or Google Pay Web is being used, it follows this flow: Confirmation -> Playback -> Submission
   */

  // This is called by Stripe after the payment intent is confirmed or the payment method is created.
  // When {shouldConfirmIntent}, it must submit the order.
  // Otherwise it must initiate order playback.
  const onStripeConfirmation = useCallback(
    (paymentIntentOrMethodId?: string) => {
      if (shouldConfirmIntent) {
        requestOrderSubmission(paymentIntentOrMethodId);
        return;
      }

      logCustomEvent('bag.place-order.selected');
      requestOrderPlayback(paymentIntentOrMethodId);
    },
    [
      logCustomEvent,
      requestOrderPlayback,
      requestOrderSubmission,
      shouldConfirmIntent,
    ],
  );

  // ─── Payment Confirmation ─────────────────────────────────────────────────

  const notifyStripeError = useNotifyStripeError(trackStripeUsage);
  const notifyStripeCancel = useNotifyStripeCancel();
  const circularMachineRefs = useRef({
    startStripeConfirmation,
    cancelRequest,
    onStripeConfirmation,
  });

  const confirmPayment = useCallback(async () => {
    circularMachineRefs.current.startStripeConfirmation();

    // Use first saved payment method if no payment method is selected from the SDK.
    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      trackStripeUsage({ event: 'confirming_with_fallback' });
      circularMachineRefs.current.onStripeConfirmation(paymentMethods[0].id);

      return;
    }

    if (total === 0) {
      trackStripeUsage({ event: 'confirming_with_zero_total' });
      circularMachineRefs.current.onStripeConfirmation(
        fallbackNoPaymentIsNeeded(paymentMethods),
      );

      return;
    }

    if (!selectedPaymentMethod) {
      trackStripeUsage({ event: 'confirming_without_payment_option' });
    }

    const result = (await confirmStripe()) as EmbeddedPaymentElementResult & {
      paymentMethodId: string;
    };

    if (result.status === 'completed') {
      trackStripeUsage({ event: 'result.completed' });
      circularMachineRefs.current.onStripeConfirmation(result.paymentMethodId);
      return;
    }

    if (result.status === 'failed') {
      trackStripeUsage({ event: 'result.failed', error: result.error });
      notifyStripeError(result.error.message ?? result.error);
      circularMachineRefs.current.cancelRequest();
      return;
    }

    if (result.status === 'canceled') {
      trackStripeUsage({ event: 'result.canceled' });
      notifyStripeCancel();
      circularMachineRefs.current.cancelRequest();
    }
  }, [
    selectedPaymentMethod,
    paymentMethods,
    total,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
  ]);

  // This is called when the customer presses the place order button.
  // When {shouldConfirmIntent}, it must initiate order playback.
  // Otherwise it must confirm the payment intent.
  const handlePlaceOrder = useCallback(() => {
    logCustomEvent('bag.place-order.selected');

    if (shouldConfirmIntent) {
      requestOrderPlayback();
      return;
    }

    confirmPayment();
  }, [
    logCustomEvent,
    shouldConfirmIntent,
    confirmPayment,
    requestOrderPlayback,
  ]);

  // ─── Post Order Playback Binding ───────────────────────────────────────────
  // These must be bound to the machine so they can be invoked after order playback.

  useEffect(() => {
    setConfirmStripe(confirmPayment);
  }, [confirmPayment, setConfirmStripe]);

  useEffect(() => {
    setIsGooglePayWeb(isGooglePayWeb);
  }, [isGooglePayWeb, setIsGooglePayWeb]);

  // ─── Order Playback ───────────────────────────────────────────────────────

  const orderPlaybackConfigFlag = useFeatureFlag('CELS-4127-order-playback-config'); // prettier-ignore
  const isOrderPlaybackRedesignEnabled = useFeatureFlag('CELS-4719-order-playback-redesign-enabled'); // prettier-ignore

  const orderPlaybackConfig = getOrderPlaybackConfig(
    orderPlaybackConfigFlag,
    isOrderPlaybackRedesignEnabled,
  );

  const handleOrderPlaybackGoBack = useCallback(() => {
    track('bag.order-playback.go-back', { orderPlaybackConfig });
    cancelRequest();
  }, [cancelRequest, orderPlaybackConfig, track]);

  // This is called when the customer skips order playback
  // The machine also does it automatically after 5 seconds.
  // If confirmation is enabled, it must confirm the payment intent.
  // When it is disabled, it must submit the order.
  // Will be removed with the CELS-4719-order-playback-redesign-enabled flag.
  const handleOrderPlaybackLooksGood = useCallback(() => {
    track('bag.order-playback.looks-good', { orderPlaybackConfig });

    if (shouldConfirmIntent) {
      confirmPayment();
      return;
    }

    requestOrderSubmission();
  }, [
    track,
    orderPlaybackConfig,
    shouldConfirmIntent,
    requestOrderSubmission,
    confirmPayment,
  ]);

  useTrackEventEffect({
    name: 'bag.order-playback.view',
    payload: { orderPlaybackConfig },
    skip: !isShowingOrderPlayback,
  });

  // ─── Auto Close Payment Selection ──────────────────────────────────────────
  // Whenever the payment method changes, send the finish payment selection event.
  // This event is only consumed if the state is changing payment method.

  useEffect(() => {
    if (!selectedPaymentMethod) return;
    finishPaymentSelection();
  }, [finishPaymentSelection, selectedPaymentMethod]);

  // ──────────────────────────────────────────────────────────────────────────

  const hasFullHeight =
    isShowingOrderPlayback &&
    (currentBreakpoint.isXS || (minWidth.isSM && currentHeight.isSM));

  const animatedDialogStyle = hasFullHeight
    ? styles.fullContentMargin
    : styles.largerContentMargin;

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <AnimatedDialog
      show={isShowingModal}
      maxHeight={isShowingCheckoutLedger ? ledgerDialogHeight : undefined}
      dismissOverlayAccessibilityLabel={dismissModal}
      style={animatedDialogStyle}
      backgroundStyle={hasFullHeight ? styles.hideBorderRadius : undefined}
      onLayout={resetScrollOffset}
      dismissModal={cancelRequest}
    >
      <SafeAreaView
        style={styles.dialogContentInnerContainer}
        edges={isShowingModal ? ['bottom'] : []}
      >
        {/* Wanted time selection */}
        {isChangingTime && wantedTime ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
              closeA11yLabel={formatMessage(messages.dismissTimeSelectionModal)} // prettier-ignore
            />

            <Bag.TimePicker
              wantedTime={wantedTime}
              wantedTimes={wantedTimes}
              changeTime={changeTime}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Delivery preferences form */}
        {isChangingDeliveryPreferences ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <BagDeliveryPreferencesForm
              deliveryOrderDetail={deliveryOrderDetail}
              deliveryPreferences={deliveryPreferences}
              onSubmit={changeDeliveryPreferences}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Stripe payment element, always mounted otherwise the SDK doesn't load. */}
        <AnimatedDialogScrollViewMounted
          show
          lockFocus={isChangingPaymentMethod}
          style={styles.stripePaymentElement}
          {...scrollViewProps}
        >
          <BagAnimatedDialogHeader
            orderState={orderState}
            scrollOffsetSV={scrollOffsetSV}
            show={isChangingPaymentMethod}
          />

          <StripePaymentElement
            show={isChangingPaymentMethod}
            embeddedPaymentElementView={embeddedPaymentElementView}
          >
            <Bag.PaymentMethodGiftCardCta onPress={requestGiftCardForm} />
          </StripePaymentElement>
        </AnimatedDialogScrollViewMounted>

        {/* Giftcard form */}
        {isShowingGiftCardForm ? (
          <AnimatedDialogScrollView
            {...scrollViewProps}
            {...Platform.select({ native: scrollViewFullHeightProps })}
          >
            <Bag.GiftCardForm
              isLoading={isApplyingGiftCard}
              onSubmit={submitGiftCardForm}
              onBack={goBack}
              onClose={cancelRequest}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Loyalty info */}
        {isShowingLoyaltyInfo ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <Loyalty.InfoBenefits
              outerContainerStyle={styles.loyaltyInfoBenefits}
              loyaltyInfoBenefits={pointsRewards}
              isLoading={isFetchingPointsRewards}
              disclaimer={benefitsDisclaimer}
              refetch={refetchPointsRewards}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Loyalty Terms */}
        {isShowingLoyaltyTerms && rewardBeingShownTerms ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <LoyaltyRewardTerms
              title={rewardBeingShownTerms.name}
              terms={rewardBeingShownTerms.terms}
              expirationDate={rewardBeingShownTerms.expirationDate}
              style={styles.termsContainer}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Next Day Order Notice */}
        {isShowingNextDayOrderNotice ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <Bag.NextDayOrderWarning
              message={nextDayOrderWarning}
              onClose={cancelRequest}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Order Playback */}
        {isShowingOrderPlayback ? (
          <AnimatedDialogView
            style={[
              styles.fullHeight,
              match([
                styles.orderPlaybackXS,
                currentHeight.isSM
                  ? styles.orderPlaybackSmallHeightSM
                  : styles.orderPlaybackSM,
              ]),
            ]}
          >
            <Bag.OrderPlayback
              products={products}
              wantedTime={ignoreTimezone(adjustWantedTime(wantedTime) || '')}
              locationName={locationName}
              isWantedTimeAsap={orderState.isWantedTimeAsap}
              orderChannel={orderChannel}
              isLoading={isSubmittingOrder}
              isPostOrder={isPostOrder}
              orderPlaybackTitle={orderPlaybackTitle}
              orderPlaybackFooterTitle={orderPlaybackFooterTitle}
              orderPlaybackFooterLoading={orderPlaybackFooterLoading}
              orderPlaybackFooterCta={orderPlaybackFooterCta}
              orderPlaybackCta={orderPlaybackCta}
              orderPlaybackConfig={orderPlaybackConfig}
              goBack={handleOrderPlaybackGoBack}
              confirmOrder={handleOrderPlaybackLooksGood}
            />
          </AnimatedDialogView>
        ) : null}

        {/* Checkout ledger - always mounted due to the hosted frame */}
        <AnimatedDialogScrollViewMounted
          show={isShowingCheckoutLedger}
          style={isShowingCheckoutLedger ? ledgerContentStyle : undefined}
          {...scrollViewProps}
        >
          <BagAnimatedDialogHeader
            orderState={orderState}
            scrollOffsetSV={scrollOffsetSV}
            show={isShowingCheckoutLedger}
          />

          {isShowingCheckoutLedger ? (
            <Bag.CheckoutContainer onLayout={measureCheckoutWithoutDisclaimers}>
              <StripePaymentMethod
                paymentMethods={paymentMethods}
                name={selectedPaymentMethod}
                imageBase64={selectedPaymentImage}
                icon={selectedPaymentIcon}
                error={stripeError}
                isDisabled={isSubmittingOrder}
                isGooglePay={isGooglePay}
                onPress={requestPaymentMethodChange}
              />

              {availableCredit > 0 ? (
                <Bag.CreditToggle
                  shouldUseCredit={shouldUseCredit}
                  availableCredit={availableCredit}
                  appliedCredit={creditToBeApplied}
                  onChange={changeCreditUsage}
                />
              ) : null}

              <BagTipping
                restaurantId={restaurantId}
                orderChannel={orderChannel}
                selectedTip={selectedTip}
                availableTips={availableTips}
                maxTipAmount={maxTipAmount}
                isDisabled={isPerformingBagAction}
                onTipChange={changeTip}
              />

              <Bag.Ledger
                ledger={ledger}
                tip={selectedTip}
                maxTipAmount={maxTipAmount}
                discountInfo={discountInfo}
                availableCredit={availableCredit}
                shouldUseCredit={shouldUseCredit}
              />

              {isGooglePayAndroid ? (
                <StripePaymentButton
                  isDisabled={isSubmissionDisabled}
                  onPress={handlePlaceOrder}
                />
              ) : (
                <Bag.CheckoutCta
                  cta={placeOrderCta}
                  paymentMethodId={selectedPaymentMethod || paymentMethods[0]?.id} // prettier-ignore
                  isLoading={isSubmittingOrder}
                  isDisabled={isSubmissionDisabled}
                  onCheckout={handlePlaceOrder}
                />
              )}
            </Bag.CheckoutContainer>
          ) : null}

          {isShowingCheckoutLedger ? (
            <View style={styles.disclaimersPadding}>
              <Bag.Disclaimers orderChannel={orderChannel} />
            </View>
          ) : null}
        </AnimatedDialogScrollViewMounted>
      </SafeAreaView>
    </AnimatedDialog>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  dismissTimeSelectionModal: {
    defaultMessage: 'Dismiss time selection modal',
    description: 'Bag | Animated Modal | Dismiss time selection modal',
  },
  dismissModal: {
    defaultMessage: 'Dismiss modal',
    description: 'Bag | Animated Modal | Dismiss modal',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  dialogContentInnerContainer: {
    flexGrow: 1,
  },
  disclaimersPadding: {
    padding: theme.spacing['8'],
    backgroundColor: theme.colors.LINEN,
  },
  fullContentMargin: {
    flexGrow: 1,
    marginTop: 0,
  },
  largerContentMargin: {
    marginTop: CUSTOM_NAVIGATION_HEADER_HEIGHT,
  },
  loyaltyInfoBenefits: {
    marginBottom: theme.spacing['10'],
  },
  termsContainer: {
    marginTop: theme.spacing['6'],
  },
  orderPlaybackXS: {
    flexGrow: 1,
    height: '100%',
  },
  orderPlaybackSM: {
    flexShrink: 1,
    minHeight: 640,
  },
  orderPlaybackSmallHeightSM: {
    flexGrow: 1,
  },
  stripePaymentElement: {
    maxHeight: 500,
  },
  fullHeight: {
    height: '100%',
  },
  hideBorderRadius: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagAnimatedDialogProps = {
  orderState: ReturnType<typeof useOrderState>;
};
